import React from "react";
import { Button, Text } from "@zorni/components-lib";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useEstablishmentData } from "../hooks/useEstablishmentData";
import { useLegalRepresentativeData } from "../hooks/useLegalRepresentative";
import { useFinancialInformationData } from "../hooks/useFinancialInformation";
import Preloader from "../../../../common/preLoader/PreLoader";
import styles from "./ViewEstablishementNew.module.css";

const ViewEstablishementNew = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { establishmentId } = useParams();

  const { establishment, loading: dataLoading } = useEstablishmentData(establishmentId);
  const { legalRepresentative, loading: legalRepLoading } = useLegalRepresentativeData(
    establishmentId,
    establishment?.legalRepresentativeId
  );
  const { financialInformation, loading: financialInfoLoading } = useFinancialInformationData(
    establishmentId,
    establishment?.financialInformationId
  );

  const notSpecified = t("commons.notSpecified");
  const fallback = (value) => (value ? value : notSpecified);

  const handlePrint = () => {
    const printClass = "kyc-view-printing";
    const cleanup = () => document.body.classList.remove(printClass);

    document.body.classList.add(printClass);
    window.addEventListener("afterprint", cleanup, { once: true });
    window.print();
  };

  if (dataLoading || legalRepLoading || financialInfoLoading) return <Preloader />;

  const company = establishment?.company;
  const registeredOffice = company?.registeredOffice;
  const tourismAgreements = establishment?.tourismAgreements;
  const insurance = tourismAgreements?.professionalLiabilityInsurance;

  const registeredOfficeValue = registeredOffice
    ? [
        registeredOffice.number,
        registeredOffice.route,
        registeredOffice.complement,
        registeredOffice.stage ? `Etage ${registeredOffice.stage}` : "",
        registeredOffice.zipPostal,
        `${registeredOffice.city || ""} (${registeredOffice.country || ""})`,
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  // Les justificatifs sont des liens (fileLink renvoyé par l'API d'upload) : on
  // n'ouvre que des URL http(s), jamais un schéma exécutable (javascript:, data:).
  const safeDocumentUrl = (value) => (/^https?:\/\//i.test(value || "") ? value : null);
  const modelJUrl = safeDocumentUrl(company?.commercialRegister?.modelJ);
  const identityDocumentUrl = safeDocumentUrl(legalRepresentative?.supportingDocumentsId);

  const managerName = [legalRepresentative?.firstName, legalRepresentative?.lastName]
    .filter(Boolean)
    .join(" ");

  return (
    <section className={styles.page}>
      <div className={styles.container}>
        <header className={styles.banner}>
          <div>
            <Text as="h1" size="xl" weight="bold" className={styles.title}>
              {t("manageEstablishment.new.view.title")}
            </Text>
            <Text as="p" size="sm" color="muted">
              {t("manageEstablishment.new.view.subtitle")}
            </Text>
          </div>
          <span className={styles.status}>{t("manageEstablishment.new.view.status")}</span>
        </header>

        <div className={styles.sections}>
          <ReadOnlySection number="01" title={t("manageEstablishment.new.view.sections.company")}>
            <Data label={t("manageEstablishment.new.fields.companyName")} value={fallback(company?.legalName)} />
            <Data label={t("manageEstablishment.new.fields.legalForm")} value={fallback(company?.legalForm)} />
            <Data label={t("manageEstablishment.new.fields.ice")} value={fallback(company?.ice)} />
            <Data label={t("manageEstablishment.new.fields.fiscalId")} value={fallback(company?.taxIdentifier)} />
            <Data label={t("manageEstablishment.new.fields.professionalTax")} value={fallback(company?.professionalTax)} />
            <Data
              label={t("manageEstablishment.new.fields.rcNumber")}
              value={fallback(
                company?.commercialRegister?.number &&
                  `${company.commercialRegister.number} (${company.commercialRegister.courtCity || ""})`
              )}
            />
            <Data wide label={t("manageEstablishment.new.fields.registeredOffice")} value={fallback(registeredOfficeValue)} />
            {modelJUrl ? (
              <Data
                wide
                label={t("manageEstablishment.new.fields.modelJ")}
                value={t("commons.buttons.viewDocument")}
                documentUrl={modelJUrl}
              />
            ) : (
              <Data wide label={t("manageEstablishment.new.fields.modelJ")} value={notSpecified} />
            )}
            <Data
              label={t("manageEstablishment.new.fields.licenseNumber")}
              value={fallback(
                tourismAgreements?.licenseNumber &&
                  `${tourismAgreements.licenseNumber} - ${tourismAgreements.issueDate || ""}`
              )}
            />
            <Data
              wide
              label={t("manageEstablishment.new.fields.insurance")}
              value={fallback(
                insurance?.company && `${insurance.company} (${insurance.policyNumber || ""} - ${insurance.expiryDate || ""})`
              )}
            />
            <Data
              wide
              label={t("manageEstablishment.new.fields.activities")}
              value={establishment?.activities?.length ? establishment.activities.join(", ") : notSpecified}
              tags={Boolean(establishment?.activities?.length)}
            />
          </ReadOnlySection>

          <ReadOnlySection number="02" title={t("manageEstablishment.new.view.sections.governance")}>
            <Data label={t("manageEstablishment.new.fields.manager")} value={fallback(managerName)} />
            <Data label={t("manageEstablishment.new.fields.nationality")} value={fallback(legalRepresentative?.nationality)} />
            <Data
              label={t("manageEstablishment.new.fields.capitalShare")}
              value={fallback(legalRepresentative?.ownershipPercentage != null ? `${legalRepresentative.ownershipPercentage} %` : "")}
            />
            <Data
              label={t("manageEstablishment.new.fields.identityDocument")}
              value={fallback(
                legalRepresentative?.identityNumber &&
                  `${legalRepresentative.identityDocumentType || ""} : ${legalRepresentative.identityNumber} - ${legalRepresentative.identityExpiryDate || ""}`
              )}
            />
            <Data wide label={t("manageEstablishment.new.fields.professionalEmail")} value={fallback(legalRepresentative?.email)} />
            {identityDocumentUrl ? (
              <Data
                wide
                label={t("manageEstablishment.new.fields.identityDocument")}
                value={t("commons.buttons.viewDocument")}
                documentUrl={identityDocumentUrl}
              />
            ) : (
              <Data wide label={t("manageEstablishment.new.fields.identityDocument")} value={notSpecified} />
            )}
          </ReadOnlySection>

          <ReadOnlySection number="03" title={t("manageEstablishment.new.view.sections.finance")}>
            <Data wide label={t("manageEstablishment.new.fields.bank")} value={fallback(financialInformation?.bankName)} />
            <Data wide label={t("manageEstablishment.new.fields.accountHolder")} value={fallback(financialInformation?.accountHolder)} />
            <Data wide highlight label={t("manageEstablishment.new.fields.rib")} value={fallback(financialInformation?.rib)} />
          </ReadOnlySection>
        </div>

        <footer className={styles.actions}>
          <Button
            variant="secondary"
            label={t("manageEstablishment.new.actions.edit")}
            onClick={() => navigate(`/admin/establishments/${establishmentId}/edit`)}
          />
          <Button
            primary
            label={t("manageEstablishment.new.actions.print")}
            onClick={handlePrint}
          />
        </footer>
      </div>
    </section>
  );
};

const ReadOnlySection = ({ number, title, children }) => (
  <section className={styles.card}>
    <div className={styles.cardHeader}>
      <span className={styles.sectionIcon}>{number}</span>
      <Text as="h2" size="lg" weight="semibold" className={styles.sectionTitle}>
        {title}
      </Text>
    </div>
    <div className={styles.dataGrid}>{children}</div>
  </section>
);

const Data = ({ label, value, wide, highlight, documentUrl, tags }) => (
  <div className={`${styles.data} ${wide ? styles.wide : ""}`}>
    <span className={styles.label}>{label}</span>
    {documentUrl ? (
      <a className={styles.document} href={documentUrl} target="_blank" rel="noopener noreferrer">
        {value}
      </a>
    ) : tags ? (
      <div className={styles.tags}>
        {value.split(", ").map((tag) => <span key={tag}>{tag}</span>)}
      </div>
    ) : (
      <span className={`${styles.value} ${highlight ? styles.highlight : ""}`}>{value}</span>
    )}
  </div>
);

export default ViewEstablishementNew;
